import { Component, OnInit, AfterViewInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import * as PDFLib from 'pdf-lib';
import { DocumentService, SignData } from 'src/app/core/services/document.service';
import { ConfirmModalService } from 'src/app/core/services/confirm-modal.service';
import { ToastService } from 'src/app/core/services/toast.service';


(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const { PDFDocument, rgb } = PDFLib;


interface SignatureBox {
  id: number;
  x: number;
  y: number;
  w: number;
  h: number;
  readonly?: boolean;
  name?: string;
}

interface SignaturePage {
  page: string;
  coordinates: Array<{
    x: number;
    y: number;
    w: number;
    h: number;
  }>;
}

@Component({
    selector: 'app-pdf-signer',
    templateUrl: './pdf-signer.component.html',
    styleUrls: ['./pdf-signer.component.scss'],
    standalone: false
})

export class PdfSignerComponent implements OnInit, AfterViewInit, OnChanges {

  @Input() signData: SignData = {
    pdfUrl: '',
    signatureDetail: null,
    name: '',
    editable: false
  };
  @Output() close = new EventEmitter<any>();

  /***********************************************************************
 * Configuration
 ***********************************************************************/
  private PDF_URL: string = "";
  private SCALE: number = 1.3;
  private SIG_W: number = 220;
  private SIG_H: number = 80;

  /***********************************************************************
   * Types
   ***********************************************************************/


  /***********************************************************************
   * Application state
   ***********************************************************************/
  private pdfDoc: any = null;
  private totalPages: number = 0;

  // signaturePositions[pageNumber] = SignatureBox[]
  private signaturePositions: Record<number, SignatureBox[]> = {};
  private sigIdCounter: number = 1;
  // ids of signatures created together by the "Same position for all pages"
  // action, so they can be collapsed back down to a single page when the
  // checkbox is unchecked.
  private sameForAllGroupIds: number[] = [];
  /***********************************************************************
 * DOM references
 ***********************************************************************/
  private viewer!: HTMLElement;
  private pageSelect!: HTMLSelectElement;
  private sameForAll!: HTMLInputElement;
  private addSignatureBtn!: HTMLButtonElement;
  private getDataBtn!: HTMLButtonElement;
  private output!: HTMLElement;
  private loadingEl!: HTMLElement | null;
  private isSavingPosition = false;

  /***********************************************************************
   * pdf.js worker
   ***********************************************************************/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any

  /***********************************************************************
 * Load PDF
 ***********************************************************************/

  constructor(
    private documentService: DocumentService,
    private confirmModalService: ConfirmModalService,
    private toastService: ToastService
  ) { }

  private async loadPdf(): Promise<void> {
    try {
      const loadingTask = pdfjsLib.getDocument({
        url: this.PDF_URL
      });

      this.pdfDoc = await loadingTask.promise;
      this.totalPages = this.pdfDoc.numPages;

      // init signature storage
      for (let p = 1; p <= this.totalPages; p++) {
        this.signaturePositions[p] = [];
      }
      this.sameForAllGroupIds = [];

      this.loadExistingSignatureDetails();
      this.loadPreviousSignatureDetails();

      // populate page selector
      this.pageSelect.innerHTML = '';
      for (let i = 1; i <= this.totalPages; i++) {
        const opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = `Page ${i}`;
        this.pageSelect.appendChild(opt);
      }

      this.loadingEl?.remove();
      await this.render();
    } catch (err: any) {
      if (this.loadingEl) {
        this.loadingEl.textContent = `Failed loading PDF: ${err.message}`;
      }
      console.error('PDF load error:', err);
    }
  }


  private loadPreviousSignatureDetails(): void {
    const index = this.signData?.signerIndex ?? 0;

    const signers = this.documentService.getSigners
      .filter((_, i) => i !== index);

    signers.forEach((signer: any) => {
      let existingDetails = signer?.signatureDetail;

      if (!existingDetails) return;

      if (!Array.isArray(existingDetails)) {
        existingDetails = [existingDetails];
      }

      existingDetails.forEach((page: any) => {
        const pageNum = Number(page.page);

        if (!pageNum || !this.signaturePositions[pageNum]) return;

        const coords = Array.isArray(page.coordinates)
          ? page.coordinates
          : [page.coordinates];

        coords.forEach((coord: any) => {
          const sig: SignatureBox = {
            id: this.sigIdCounter++,
            x: this.pointToPx(Number(coord.x) || 0),
            y: this.pointToPx(Number(coord.y) || 0),
            w: this.pointToPx(Number(coord.w) || this.SIG_W),
            h: this.pointToPx(Number(coord.h) || this.SIG_H),
            readonly: true,
            name: signer.name
          };

          this.signaturePositions[pageNum].push(sig);
        });
      });
    });
  }

  private loadExistingSignatureDetails(): void {
    let existingDetails =
      this.signData?.signatureDetail;

    if (!existingDetails) {
      return;
    }

    if (!Array.isArray(existingDetails)) {
      existingDetails = [existingDetails];
    }

    this.signatureData = existingDetails;

    existingDetails.forEach((page: any) => {
      const pageNum = Number(page.page);
      if (!pageNum || !this.signaturePositions[pageNum]) {
        return;
      }

      const coords = Array.isArray(page.coordinates) ? page.coordinates : [page.coordinates];
      coords.forEach((coord: any) => {
        const sig: SignatureBox = {
          id: this.sigIdCounter++,
          x: this.pointToPx(Number(coord.x) || 0),
          y: this.pointToPx(Number(coord.y) || 0),
          w: this.pointToPx(Number(coord.w) || this.SIG_W),
          h: this.pointToPx(Number(coord.h) || this.SIG_H)
        };

        this.signaturePositions[pageNum].push(sig);
      });
    });
  }

  private pxToPoints(value: number): number {
    return Number((value / this.SCALE).toFixed(2));
  }

  private pointToPx(value: number): number {
    return Math.round(value * this.SCALE);
  }


  /***********************************************************************
 * Rendering
 ***********************************************************************/
  private async render(): Promise<void> {
    this.viewer.innerHTML = "";
    const showAll = this.sameForAll.checked;

    if (showAll) {
      for (let p = 1; p <= this.totalPages; p++) {
        await this.renderPage(p);
      }
    } else {
      const sel = Number(this.pageSelect.value || 1);
      await this.renderPage(sel);
    }
  }

  private async renderPage(pageNum: number): Promise<void> {
    const page = await this.pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: this.SCALE });

    const pageWrap = document.createElement("div");
    pageWrap.className = "page-wrap";
    pageWrap.dataset['page'] = String(pageNum);
    pageWrap.style.position = 'relative'



    const canvas = document.createElement("canvas");
    canvas.className = "pdf-canvas";
    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.style.display = 'block'
    canvas.style.borderRadius = '6px'
    canvas.style.maxWidth = '100%'
    canvas.style.height = 'auto'
    canvas.style.boxShadow = ' 0 2px 8px rgba(8, 12, 24, 0.04)'
    canvas.style.background = '#fff'
    canvas.style.background = '1px solid #e5e7eb'
    canvas.style.left = '20px';

    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.style.position = 'absolute';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = `${canvas.width}px`;
    overlay.style.height = `${canvas.height}px`;
    overlay.style.pointerEvents = 'none';


    const ctx = canvas.getContext("2d")!;
    await page.render({ canvasContext: ctx, viewport }).promise;

    pageWrap.append(canvas, overlay);
    this.viewer.appendChild(pageWrap);

    pageWrap.addEventListener("dblclick", async (ev: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const y = ev.clientY - rect.top;
      await this.addSignatureAt(pageNum, x - this.SIG_W / 2, y - this.SIG_H / 2);
    });

    this.signaturePositions[pageNum].forEach(sig =>
      this.placeSigDom(pageWrap, overlay, sig)
    );
  }

  /***********************************************************************
   * Signature logic
   ***********************************************************************/
  private async addSignatureAt(pageNum: number, x: number, y: number): Promise<void> {
    const pageWrap = this.viewer.querySelector(
      `.page-wrap[data-page='${pageNum}']`
    ) as HTMLElement | null;
    if (!pageWrap) return;

    const canvas = pageWrap.querySelector("canvas")!;
    const rect = canvas.getBoundingClientRect();

    const nx = Math.max(0, Math.min(x, rect.width - this.SIG_W));
    const ny = Math.max(0, Math.min(y, rect.height - this.SIG_H));

    if (this.sameForAll.checked) {
      const sharedId = this.sigIdCounter++;
      for (let p = 1; p <= this.totalPages; p++) {
        this.signaturePositions[p].push({
          id: sharedId,
          x: Math.round(nx),
          y: Math.round(ny),
          w: this.SIG_W,
          h: this.SIG_H,
          readonly: false
        });
      }
      this.sameForAllGroupIds.push(sharedId);
      await this.render();
    } else {
      const sig: SignatureBox = {
        id: this.sigIdCounter++,
        x: Math.round(nx),
        y: Math.round(ny),
        w: this.SIG_W,
        h: this.SIG_H,
        readonly: false
      };
      this.signaturePositions[pageNum].push(sig);
      this.placeSigDom(pageWrap, pageWrap.querySelector(".overlay")!, sig);
    }
  }
  private placeSigDom(
    pageWrap: HTMLElement,
    overlay: HTMLElement,
    sig: SignatureBox
  ): void {
    if (overlay.querySelector(`.sig-box[data-id='${sig.id}']`)) return;

    const box = document.createElement('div');

    box.className = 'sig-box';
    box.dataset['id'] = String(sig.id);

    const isReadonly = !!sig.readonly;
    box.textContent = isReadonly
      ? (sig.name || 'PREVIEW')
      : (this.signData.name || 'SIGN HERE');
    box.title = `x:${this.pxToPoints(sig.x)} y:${this.pxToPoints(sig.y)} w:${this.pxToPoints(sig.w)} h:${this.pxToPoints(sig.h)} pts`;

    // 📍 position
    box.style.position = 'absolute';
    box.style.left = `${sig.x}px`;
    box.style.top = `${sig.y}px`;
    box.style.width = `${sig.w || this.SIG_W}px`;
    box.style.height = `${sig.h || this.SIG_H}px`;
    box.style.zIndex = '1000';

    // layout
    box.style.display = 'flex';
    box.style.alignItems = 'center';
    box.style.justifyContent = 'center';

    box.style.boxSizing = 'border-box';
    box.style.borderRadius = '6px';
    box.style.fontWeight = '700';
    box.style.userSelect = 'none';
    box.style.padding = '8px 28px 8px 10px';

    if (isReadonly) {
      box.style.border = '2px dashed #999';
      box.style.background = 'rgba(128,128,128,0.25)';
      box.style.color = '#555';
      box.style.cursor = 'not-allowed';
      box.style.pointerEvents = 'none';

    } else if (this.signData.editable) {
      box.style.border = '2px dashed rgba(13,110,253,0.85)';
      box.style.background = 'rgba(210, 226, 255, 0.95)';
      box.style.color = '#06306e';
      box.style.cursor = 'grab';
      box.style.pointerEvents = 'auto';

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.textContent = '×';

      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '4px';
      closeBtn.style.right = '4px';
      closeBtn.style.width = '20px';
      closeBtn.style.height = '20px';
      closeBtn.style.border = 'none';
      closeBtn.style.borderRadius = '50%';
      closeBtn.style.background = 'rgba(13,110,253,0.9)';
      closeBtn.style.color = '#fff';
      closeBtn.style.cursor = 'pointer';
      closeBtn.style.textAlign = 'center';
      closeBtn.style.lineHeight = '18px';
      closeBtn.style.fontSize = '14px';
      closeBtn.style.padding = '0';
      closeBtn.style.zIndex = '1001';
      closeBtn.style.userSelect = 'none';

      closeBtn.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.removeSignature(pageWrap, sig);
      });

      box.appendChild(closeBtn);

      const resizeHandle = document.createElement('div');
      resizeHandle.className = 'sig-resize-handle';

      resizeHandle.style.position = 'absolute';
      resizeHandle.style.width = '18px';
      resizeHandle.style.height = '18px';
      resizeHandle.style.right = '4px';
      resizeHandle.style.bottom = '4px';
      resizeHandle.style.cursor = 'se-resize';
      resizeHandle.style.zIndex = '1001';
      resizeHandle.style.userSelect = 'none';
      resizeHandle.style.background = `
        linear-gradient(135deg, transparent 65%, #fff 65%),
        linear-gradient(135deg, transparent 80%, #fff 80%),
        linear-gradient(135deg, transparent 90%, #fff 90%)
      `;
      resizeHandle.style.backgroundSize = '100% 100%';
      resizeHandle.style.backgroundRepeat = 'no-repeat';

      resizeHandle.addEventListener('mousedown', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.makeResizableSignBox(box, pageWrap, sig, e);
      });

      resizeHandle.addEventListener('touchstart', (e: TouchEvent) => {
        e.preventDefault();
        e.stopPropagation();
        this.makeResizableSignBox(box, pageWrap, sig, e);
      });

      box.appendChild(resizeHandle);

      //  draggable only for editable
      this.makeDraggable(box, pageWrap, sig);
    } else {
      // 🔎 PREVIEW-only style for non-editable current signer boxes
      box.style.border = '2px dashed rgba(156, 163, 175, 0.75)';
      box.style.background = 'rgba(243, 244, 246, 0.95)';
      box.style.color = '#374151';
      box.style.cursor = 'default';
      box.style.pointerEvents = 'none';
    }

    overlay.appendChild(box);
  }
  private makeDraggable(
    box: HTMLElement,
    pageWrap: HTMLElement,
    sigObj: SignatureBox
  ): void {

    let startMouseX = 0;
    let startMouseY = 0;
    let startX = 0;
    let startY = 0;

    const canvas = pageWrap.querySelector('canvas')!;

    const onStart = (e: MouseEvent | TouchEvent) => {
      const target = (e.target as HTMLElement);
      if (target.closest('button') || target.closest('.sig-resize-handle')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const isTouch = e.type.startsWith('touch');
      const clientX = isTouch ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = isTouch ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

      startMouseX = clientX;
      startMouseY = clientY;
      startX = sigObj.x;
      startY = sigObj.y;

      document.body.style.userSelect = 'none';

      const onMove = (ev: MouseEvent | TouchEvent) => {
        const moveClientX = isTouch ? (ev as TouchEvent).touches[0].clientX : (ev as MouseEvent).clientX;
        const moveClientY = isTouch ? (ev as TouchEvent).touches[0].clientY : (ev as MouseEvent).clientY;

        let newX = startX + (moveClientX - startMouseX);
        let newY = startY + (moveClientY - startMouseY);

        newX = Math.max(0, Math.min(newX, canvas.width - sigObj.w));
        newY = Math.max(0, Math.min(newY, canvas.height - sigObj.h));

        sigObj.x = Math.round(newX);
        sigObj.y = Math.round(newY);

        box.style.left = sigObj.x + 'px';
        box.style.top = sigObj.y + 'px';
        box.title = `x:${this.pxToPoints(sigObj.x)} y:${this.pxToPoints(sigObj.y)} w:${this.pxToPoints(sigObj.w)} h:${this.pxToPoints(sigObj.h)} pts`;
      };

      const onUp = () => {
        document.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
        document.removeEventListener(isTouch ? 'touchend' : 'mouseup', onUp);
        document.body.style.userSelect = '';
      };

      document.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
      document.addEventListener(isTouch ? 'touchend' : 'mouseup', onUp);
    };

    box.addEventListener('mousedown', onStart);
    box.addEventListener('touchstart', onStart);

    /* ---------------- DELETE (double click) ---------------- */

    box.addEventListener('dblclick', (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      this.confirmModalService.open(
        'Remove Signature Position',
        'Are you sure you want to remove this signature position?',
        () => this.removeSignature(pageWrap, sigObj),
        'Remove',
        'Cancel'
      );
    });
  }

  private makeResizableSignBox(
    box: HTMLElement,
    pageWrap: HTMLElement,
    sigObj: SignatureBox,
    startEvent: MouseEvent | TouchEvent
  ): void {
    const canvas = pageWrap.querySelector('canvas')!;
    const minW = 220;
    const minH = 80;
    const maxW = 360;
    const maxH = 180;

    const isTouch = startEvent.type.startsWith('touch');
    let startMouseX = isTouch ? (startEvent as TouchEvent).touches[0].clientX : (startEvent as MouseEvent).clientX;
    let startMouseY = isTouch ? (startEvent as TouchEvent).touches[0].clientY : (startEvent as MouseEvent).clientY;
    let startW = sigObj.w;
    let startH = sigObj.h;

    const onMove = (ev: MouseEvent | TouchEvent) => {
      const clientX = isTouch ? (ev as TouchEvent).touches[0].clientX : (ev as MouseEvent).clientX;
      const clientY = isTouch ? (ev as TouchEvent).touches[0].clientY : (ev as MouseEvent).clientY;

      let newW = startW + (clientX - startMouseX);
      let newH = startH + (clientY - startMouseY);

      newW = Math.max(minW, Math.min(newW, Math.min(maxW, canvas.width - sigObj.x)));
      newH = Math.max(minH, Math.min(newH, Math.min(maxH, canvas.height - sigObj.y)));

      sigObj.w = Math.round(newW);
      sigObj.h = Math.round(newH);

      box.style.width = sigObj.w + 'px';
      box.style.height = sigObj.h + 'px';
      box.title = `x:${this.pxToPoints(sigObj.x)} y:${this.pxToPoints(sigObj.y)} w:${this.pxToPoints(sigObj.w)} h:${this.pxToPoints(sigObj.h)} pts`;
    };

    const onUp = () => {
      document.removeEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
      document.removeEventListener(isTouch ? 'touchend' : 'mouseup', onUp);
      document.body.style.userSelect = '';
    };

    document.body.style.userSelect = 'none';
    document.addEventListener(isTouch ? 'touchmove' : 'mousemove', onMove);
    document.addEventListener(isTouch ? 'touchend' : 'mouseup', onUp);
  }

  private removeSignature(
    pageWrap: HTMLElement,
    sigObj: SignatureBox
  ): void {
    // Always remove from ALL pages where this signature exists
    // (don't rely on sameForAll checkbox state during removal)
    for (let p = 1; p <= this.totalPages; p++) {
      this.signaturePositions[p] = this.signaturePositions[p].filter(s => s.id !== sigObj.id);
    }
    this.sameForAllGroupIds = this.sameForAllGroupIds.filter(id => id !== sigObj.id);

    this.render();
  }

  /**
   * Collapses every "Same position for all pages" group down to a single
   * page (the one currently selected) when the checkbox is unchecked, so
   * the signature no longer lingers on the other pages.
   */
  private collapseSameForAllGroups(): void {
    if (!this.sameForAllGroupIds.length) return;

    const keepPage = Number(this.pageSelect.value || 1);

    this.sameForAllGroupIds.forEach(id => {
      for (let p = 1; p <= this.totalPages; p++) {
        if (p === keepPage) continue;
        this.signaturePositions[p] = this.signaturePositions[p].filter(s => s.id !== id);
      }
    });

    this.sameForAllGroupIds = [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.signData && !changes.signData.firstChange) {
      this.PDF_URL = this.signData?.pdfUrl;
      if (this.viewer && this.pageSelect) {
        this.loadPdf().catch(err => console.error('PDF reload error:', err));
      }
    }
  }

  ngOnInit(): void {
    this.PDF_URL = this.signData?.pdfUrl;
  }

  ngAfterViewInit(): void {
    this.initView();
  }

  initView(): void {
    this.initialize();
  }


  private async initialize(): Promise<void> {
    // set DOM refs
    this.viewer = document.getElementById('viewer') as HTMLElement;
    this.pageSelect = document.getElementById('pageSelect') as HTMLSelectElement;
    this.sameForAll = document.getElementById('sameForAll') as HTMLInputElement;
    this.addSignatureBtn = document.getElementById('addSignatureBtn') as HTMLButtonElement;
    this.getDataBtn = document.getElementById('getData') as HTMLButtonElement;
    this.output = document.getElementById('output') as HTMLElement;
    this.loadingEl = document.getElementById('loading');

    // load pdf
    await this.loadPdf();

    // add event listeners
    this.sameForAll.addEventListener('change', async () => {
      // When turning the checkbox off, collapse any "apply to all pages"
      // signatures back down to the currently selected page instead of
      // leaving them scattered across every page.
      if (!this.sameForAll.checked) {
        this.collapseSameForAllGroups();
      }
      await this.render();
    });

    this.pageSelect.addEventListener('change', async () => {
      if (!this.sameForAll.checked) await this.render();
    });

    this.addSignatureBtn.addEventListener('click', async () => {
      const page = this.sameForAll.checked ? 1 : Number(this.pageSelect.value || 1);
      const wrap = this.viewer.querySelector(`.page-wrap[data-page='${page}']`) as HTMLElement | null;
      if (!wrap) return;

      const canvas = wrap.querySelector('canvas')!;
      const rect = canvas.getBoundingClientRect();
      await this.addSignatureAt(page, rect.width / 2 - this.SIG_W / 2, rect.height / 2 - this.SIG_H / 2);
    });


    this.getDataBtn.addEventListener('click', async () => {
      const newSignatures: { signaturedetails: SignaturePage[] } = {
        signaturedetails: []
      };

      for (let p = 1; p <= this.totalPages; p++) {
        const allBoxes = this.signaturePositions[p] || [];
        const editableBoxes = allBoxes.filter(s => !s.readonly);
        if (!editableBoxes.length) continue;

        newSignatures.signaturedetails.push({
          page: p.toString(),
          coordinates: editableBoxes.map(s => ({
            x: this.pxToPoints(s.x),
            y: this.pxToPoints(s.y),
            w: this.pxToPoints(s.w),
            h: this.pxToPoints(s.h)
          }))
        });
      }

      if (!newSignatures.signaturedetails.length) {
        return;
      }

      this.confirmModalService.open(
        'Confirm Save Position',
        'Are you sure you want to save the signature positions?',
        () => {
          if (this.isSavingPosition) {
            return;
          }

          this.isSavingPosition = true;

          // Completely rebuild signatureData (don't merge, replace entirely)
          this.signatureData = newSignatures.signaturedetails;

          this.toastService.showToaster(
            'Success',
            'Signature position saved successfully.',
            'success'
          );
          this.isSavingPosition = false;
        },
        'Save',
        'Cancel'
      );
    });
  }

  signatureData: any = [];
  previewPdfUrl: any = null;


  saveDoc() {
    if (this.signData.navigate === 'template') {
      this.close.emit({
        signatureData: this.signatureData,
        signerIndex: this.signData?.signerIndex,
        // pdfUrl: this.previewPdfUrl 
      });
    } else {
      this.close.emit({
        stepIndex: 2,
        signatureData: this.signatureData,
        signerName: this.signData.name,
        signerIndex: this.signData?.signerIndex,
        // pdfUrl: this.previewPdfUrl
      });
    }
  }

  exitDoc() {
    this.close.emit({
      stepIndex: 2,
    });
  }

  async generatePreviewPdf() {
    const existingPdfBytes = await fetch(this.PDF_URL).then(r =>
      r.arrayBuffer()
    );

    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const pages = pdfDoc.getPages();

    // Draw signature boxes into REAL PDF
    this.signatureData.forEach((p: any) => {
      const pageIndex = Number(p.page) - 1;
      const page = pages[pageIndex];
      if (!page) return;

      const pageHeight = page.getHeight();

      p.coordinates.forEach((c: any) => {
        const pdfX = c.x;
        const pdfY = pageHeight - c.y - c.h * 1.2;

        page.drawRectangle({
          x: pdfX,
          y: pdfY,
          width: c.w,
          height: c.h,
          borderWidth: 0.8,
          borderColor: rgb(0.85, 0.85, 0.85)
        });

        const fontSize = 9;
        const signerName = this.signData?.name ?? '';
        const text = signerName
          ? `Sign here – ${signerName}`
          : 'Sign here';

        page.drawText(text, {
          x: pdfX + 10,
          y: pdfY + c.h / 2 - fontSize / 2,
          size: fontSize,
          color: rgb(0.75, 0.75, 0.75)
        });
      });

    });

    const updatedPdfBytes = await pdfDoc.save();

    const uint8 = updatedPdfBytes instanceof Uint8Array
      ? updatedPdfBytes
      : new Uint8Array(updatedPdfBytes);

    const buffer = new ArrayBuffer(uint8.byteLength);
    new Uint8Array(buffer).set(uint8);

    const blob = new Blob([buffer], { type: 'application/pdf' });
    this.previewPdfUrl = URL.createObjectURL(blob);
    this.saveDoc();
  }
}
